import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { createDemoApi, type DemoApi } from '../api/client';
import { clearSelection } from '../selection/storage';
import {
  clearSession,
  customerIdFromEmail,
  loadSession,
  saveSession,
  type DemoSession,
} from './storage';

interface SessionContextValue {
  session: DemoSession | null;
  api: DemoApi;
  signIn: (passcode: string, email: string) => Promise<void>;
  signOut: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<DemoSession | null>(() => loadSession());

  const api = useMemo(
    () =>
      createDemoApi({
        getPasscode: () => loadSession()?.passcode ?? session?.passcode,
      }),
    [session?.passcode],
  );

  const signIn = useCallback(
    async (passcode: string, email: string) => {
      const normalizedEmail = email.trim().toLowerCase();
      const probeApi = createDemoApi({ getPasscode: () => passcode });

      try {
        const result = await probeApi.createSession({ email: normalizedEmail });
        const next: DemoSession = {
          passcode,
          email: result.email,
          customerId: result.customerId,
        };
        saveSession(next);
        setSession(next);
        return;
      } catch (err) {
        // Re-throw auth / network / bad-request; allow 501 until TASK-07 lands.
        const code =
          err && typeof err === 'object' && 'code' in err
            ? String((err as { code: string }).code)
            : '';
        if (code === 'INVALID_PASSCODE') throw err;
        if (code !== 'NOT_IMPLEMENTED') throw err;
      }

      const next: DemoSession = {
        passcode,
        email: normalizedEmail,
        customerId: customerIdFromEmail(normalizedEmail),
      };
      saveSession(next);
      setSession(next);
    },
    [],
  );

  const signOut = useCallback(() => {
    clearSession();
    clearSelection();
    setSession(null);
  }, []);

  const value = useMemo(
    () => ({ session, api, signIn, signOut }),
    [session, api, signIn, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
}
